// SiteFlow SSO handoff helpers (pure; no network, no DOM writes).
//
// Employees sign in at SiteFlow (JumpCloud SAML). SiteFlow then sends the
// browser back to the gallery with a short-lived signed handoff in the URL
// fragment, which never reaches nginx or any server log:
//
//   /#sso=siteflow-v1&user=<urlencoded email>&ts=<unix seconds>&nonce=<32 hex>&sig=<64 hex>
//
// The app parses it with parseSiteflowSsoFragment, clears the fragment, and
// posts the values to the backend's /auth/siteflow-sso for verification.

export const SSO_FRAGMENT_PREFIX = '#sso=siteflow-v1';
export const SSO_VERSION = 'siteflow-v1';
export const DEFAULT_SITEFLOW_URL = 'https://siteflow.myadvicesites.com';
export const SSO_ERROR_MESSAGE = 'Sign-in link is invalid or expired. Please try again.';
export const SSO_UNAVAILABLE_MESSAGE = 'Sign-in is not available right now. Please try again later.';

const FIELDS = ['sso', 'user', 'ts', 'nonce', 'sig'];
const TS_RE = /^[0-9]{1,12}$/;
const NONCE_RE = /^[0-9a-f]{32}$/;
const SIG_RE = /^[0-9a-f]{64}$/;
const EMAIL_RE = /^[^@\s]+@[^@\s]+$/;
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1']);

// True when the fragment is (or claims to be) a SiteFlow handoff. Such a
// fragment must be cleared from the address bar even if it fails to parse.
export const isSiteflowSsoFragment = (hash) =>
  typeof hash === 'string' && hash.startsWith(SSO_FRAGMENT_PREFIX);

// Returns {user, ts, nonce, sig} for a well-formed handoff fragment, else null.
// Values are percent-decoded with decodeURIComponent (a literal "+" stays "+",
// so plus-addressed emails survive); the backend re-validates everything.
export const parseSiteflowSsoFragment = (hash) => {
  if (!isSiteflowSsoFragment(hash)) return null;

  const values = {};
  for (const part of hash.slice(1).split('&')) {
    if (!part) continue;
    const eq = part.indexOf('=');
    if (eq === -1) return null;
    let key;
    let value;
    try {
      key = decodeURIComponent(part.slice(0, eq));
      value = decodeURIComponent(part.slice(eq + 1));
    } catch (e) {
      return null; // malformed percent-encoding
    }
    if (!FIELDS.includes(key)) continue;
    if (Object.prototype.hasOwnProperty.call(values, key)) return null; // duplicated field
    values[key] = value;
  }

  const { sso, user, ts, nonce, sig } = values;
  if (sso !== SSO_VERSION) return null;
  if (!user || user.length > 254 || !EMAIL_RE.test(user)) return null;
  if (!ts || !TS_RE.test(ts)) return null;
  if (!nonce || !NONCE_RE.test(nonce)) return null;
  if (!sig || !SIG_RE.test(sig)) return null;

  return { user, ts, nonce, sig };
};

// Where the "Sign in with SiteFlow" button goes. The base URL comes from the
// build-time REACT_APP_SITEFLOW_URL (default: production SiteFlow) and must be
// https, or http only for localhost/127.0.0.1. Returns null when invalid.
export const getSiteflowLoginUrl = (rawBase = process.env.REACT_APP_SITEFLOW_URL) => {
  const base = (rawBase || '').trim() || DEFAULT_SITEFLOW_URL;
  let url;
  try {
    url = new URL(base);
  } catch (e) {
    return null;
  }
  const secure = url.protocol === 'https:';
  const local = url.protocol === 'http:' && LOCAL_HOSTS.has(url.hostname);
  if ((!secure && !local) || url.username || url.password) return null;

  const path = url.pathname.replace(/\/+$/, '');
  return `${url.origin}${path}/auth/gallery/login/`;
};
